import {toast} from 'react-toastify';
import {ChevronUpIcon} from '@heroicons/react/24/solid';
import {updateBrand, VerdocsEndpoint, type IBrand} from '@verdocs/js-sdk';
import {FC, ReactElement, useEffect, useMemo, useRef, useState} from 'react';
import {ClipboardDocumentIcon, SwatchIcon} from '@heroicons/react/24/outline';
import {BUILDER_FONTS, BUILDER_SECTIONS, applyTheme, generateCss, normalizeHex, seedFromBrand} from './BrandBuilderState';
import {invalidateAllQueries, useBrand} from '../../react-sdk/queries';
import {BuilderFrame, LeaveDialog} from './BuilderFrame';
import type {IBuilderState} from './BrandBuilderState';
import {track} from '../../Managers/Analytics';
import {BrandPreview} from './BrandPreview';
import {BRAND_THEMES} from './BrandThemes';
import {Spinner} from '../../Components';
import {AppState} from '../../AppState';

// Drafts are local until Publish (a settled rule: nothing goes live implicitly, and there is no
// auto-save). The API has no draft column yet, so an explicitly saved draft lives in
// localStorage, keyed per brand, and is cleared on publish or discard.
const draftKey = (brandId: string) => `verdocs.brandDraft.${brandId}`;

interface BrandBuilderProps {
  brandId: string;
  onBack: () => void;
}

export const ColorControl: FC<{label: string; value: string; onChange: (v: string) => void}> = ({label, value, onChange}) => {
  const [text, setText] = useState(value);
  useEffect(() => setText(value), [value]);

  return (
    <div className="mb-3.5">
      <label className="mb-1.5 block text-xs font-medium text-gray-600">{label}</label>
      <div className="flex items-center gap-2 rounded-lg border border-gray-200 p-1">
        <input
          type="color"
          value={value}
          onChange={(e) => onChange(e.target.value.toUpperCase())}
          className="h-7 w-9 cursor-pointer rounded border-0 bg-transparent p-0"
        />
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          onBlur={() => {
            const normalized = normalizeHex(text);
            if (normalized) onChange(normalized);
            else setText(value);
          }}
          className="w-full min-w-0 border-0 font-mono text-xs uppercase text-gray-700 focus:outline-none"
        />
      </div>
    </div>
  );
};

export const InheritControl: FC<{label: string; value: string | null; seed: string; note: string; onChange: (v: string | null) => void}> = ({
  label,
  value,
  seed,
  note,
  onChange,
}) => (
  <div className="mb-3.5">
    <label className="mb-1.5 flex items-center justify-between text-xs font-medium text-gray-600">
      {label}
      <span className="flex items-center gap-1.5 text-[11px] font-normal text-gray-400">
        Override
        <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked ? seed : null)} className="cursor-pointer" />
      </span>
    </label>
    {value ? (
      <ColorControl label="" value={value} onChange={onChange} />
    ) : (
      <p className="text-[11px] leading-relaxed text-gray-400">{note}</p>
    )}
  </div>
);

export const SliderControl: FC<{label: string; value: number; min: number; max: number; display: string; onChange: (v: number) => void}> = ({
  label,
  value,
  min,
  max,
  display,
  onChange,
}) => (
  <div className="mb-3.5">
    <label className="mb-1.5 flex items-center justify-between text-xs font-medium text-gray-600">
      {label}
      <span className="font-mono text-[11px] text-gray-400">{display}</span>
    </label>
    <input
      type="range"
      min={min}
      max={max}
      value={value}
      onChange={(e) => onChange(parseInt(e.target.value, 10))}
      className="w-full accent-indigo-600"
    />
  </div>
);

export const BrandBuilder: FC<BrandBuilderProps> = ({brandId, onBack}) => {
  const organizationId = AppState.currentProfile?.organization_id || '';
  const brandQuery = useBrand(organizationId, brandId);
  const brand = brandQuery.data;

  const [published, setPublished] = useState<IBuilderState | null>(null);
  const [draft, setDraft] = useState<IBuilderState | null>(null);
  const [openSection, setOpenSection] = useState<string>('general');
  const [search, setSearch] = useState('');
  const [themeName, setThemeName] = useState('Custom');
  const [themeOpen, setThemeOpen] = useState(false);
  const [cssOpen, setCssOpen] = useState(false);
  const [working, setWorking] = useState(false);
  // What the saved draft holds, so closing only warns about changes made since the last save.
  const [savedDraftJson, setSavedDraftJson] = useState<string | null>(null);
  const [pendingLeave, setPendingLeave] = useState<(() => void) | null>(null);
  const themeSnapshot = useRef<{state: IBuilderState; themeName: string} | null>(null);

  // Seed once the brand arrives: published state mirrors what is live, the draft starts as a copy
  // unless an explicitly saved draft is waiting.
  useEffect(() => {
    if (!brand || published) return;
    const seeded = seedFromBrand(brand);
    setPublished(seeded);

    let restored: IBuilderState | null = null;
    try {
      const saved = localStorage.getItem(draftKey(brand.id));
      if (saved) {
        const parsed = JSON.parse(saved) as IBuilderState;
        // Drafts saved before the editor narrowed to visual settings still carry content fields
        // (name, footer links, disclosure text). Only keys the editor owns come back, so publishing
        // an old draft cannot overwrite what the Signing tab has saved since.
        if (parsed && typeof parsed.primary === 'string') {
          restored = {...seeded};
          (Object.keys(seeded) as (keyof IBuilderState)[]).forEach((key) => {
            if (key in parsed) (restored as any)[key] = (parsed as any)[key];
          });
        }
      }
    } catch {
      restored = null;
    }

    setDraft(restored || seeded);
    if (restored) setSavedDraftJson(JSON.stringify(restored));
    if (restored) toast.info('Restored your saved draft. Publish when you are ready, or discard it.');
  }, [brand, published]);

  const css = useMemo(() => (draft ? generateCss(draft) : ''), [draft]);


  if (!brand || !draft || !published) {
    return (
      <div className="fixed inset-0 z-40 flex items-center justify-center bg-white">
        <Spinner mode="dark" />
      </div>
    );
  }

  const dirty = JSON.stringify(draft) !== JSON.stringify(published);
  const unsaved = dirty && JSON.stringify(draft) !== savedDraftJson;

  // The previews render from the draft's generated CSS, exactly as the signing experience will
  // once it is published.
  const previewBrand: IBrand = {
    ...brand,
    primary_color: draft.primary,
    secondary_color: draft.secondary,
    style_overrides: css,
  };


  const edit = (patch: Partial<IBuilderState>) => {
    setDraft((prev) => (prev ? {...prev, ...patch} : prev));
    setThemeName((prev) => (prev === 'Custom' || prev.startsWith('Custom (') ? prev : `Custom (based on ${prev})`));
  };

  const handleSaveDraft = () => {
    try {
      localStorage.setItem(draftKey(brand.id), JSON.stringify(draft));
      setSavedDraftJson(JSON.stringify(draft));
      toast.success('Draft saved. It stays private to this builder until you publish.');
    } catch {
      toast.error('Could not save the draft in this browser.');
    }
  };

  const handleDiscard = () => {
    setDraft(published);
    setThemeName('Custom');
    setSavedDraftJson(null);
    try {
      localStorage.removeItem(draftKey(brand.id));
    } catch {
      // A blocked localStorage is not worth interrupting the user over.
    }
    toast.info('Draft discarded. Back to the published version.');
  };

  const handlePublish = async () => {
    setWorking(true);
    try {
      // Only the visual fields: names, footer links, and disclosure text belong to the Signing tab.
      await updateBrand(VerdocsEndpoint.getDefault(), organizationId, brand.id, {
        primary_color: draft.primary,
        secondary_color: draft.secondary,
        style_overrides: css,
      });

      setPublished(draft);
      setSavedDraftJson(null);
      try {
        localStorage.removeItem(draftKey(brand.id));
      } catch {
        // Nothing to recover from; the publish already succeeded.
      }
      invalidateAllQueries();
      track('brand_published', {org_id: organizationId, theme: themeName});
      toast.success('Published. New envelopes will use this look.');
    } catch (e: any) {
      toast.error(`Error publishing: ${e.response?.data?.error || e.message}`);
    }
    setWorking(false);
  };

  const openThemes = () => {
    themeSnapshot.current = {state: draft, themeName};
    setThemeOpen(true);
  };

  const revertThemes = () => {
    if (themeSnapshot.current) {
      setDraft(themeSnapshot.current.state);
      setThemeName(themeSnapshot.current.themeName);
    }
    setThemeOpen(false);
  };

  const sectionDirtyCount = (keys: (keyof IBuilderState)[]) =>
    keys.filter((k) => JSON.stringify(draft[k]) !== JSON.stringify(published[k])).length;

  const query = search.trim().toLowerCase();
  const shows = (keywords: string) => !query || keywords.toLowerCase().includes(query);
  const logoUrl = brand.thumbnail_url || brand.full_logo_url;

  const controls: Record<string, {keywords: string; node: ReactElement}[]> = {
    general: [
      {
        keywords: 'primary color main accent',
        node: <ColorControl key="primary" label="Primary color" value={draft.primary} onChange={(v) => edit({primary: v})} />,
      },
      {
        keywords: 'secondary color accent',
        node: <ColorControl key="secondary" label="Secondary color" value={draft.secondary} onChange={(v) => edit({secondary: v})} />,
      },
      {
        keywords: 'font family typeface typography',
        node: (
          <div className="mb-3.5" key="font">
            <label className="mb-1.5 block text-xs font-medium text-gray-600">Font</label>
            <select
              value={draft.font}
              onChange={(e) => edit({font: e.target.value})}
              className="w-full rounded-lg border border-gray-200 px-2.5 py-1.5 text-sm focus:border-indigo-400 focus:outline-none">
              {!BUILDER_FONTS.some((f) => f.value === draft.font) && <option value={draft.font}>Current: {draft.font}</option>}
              {BUILDER_FONTS.map((f) => (
                <option key={f.value} value={f.value}>
                  {f.label}
                </option>
              ))}
            </select>
          </div>
        ),
      },
    ],
    disclosures: [
      {
        keywords: 'dialog header accent top bar color disclosures',
        node: (
          <InheritControl
            key="dialogAccent"
            label="Dialog header accent"
            value={draft.dialogAccent}
            seed={draft.primary}
            note="Inherits your primary color."
            onChange={(v) => edit({dialogAccent: v})}
          />
        ),
      },
      {
        keywords: 'link color disclosures agreements',
        node: (
          <InheritControl
            key="linkColor"
            label="Link color"
            value={draft.linkColor}
            seed={draft.primary}
            note="Inherits your primary color."
            onChange={(v) => edit({linkColor: v})}
          />
        ),
      },
      {
        keywords: 'checkbox accept color',
        node: (
          <ColorControl key="checkboxColor" label="Checkbox color" value={draft.checkboxColor} onChange={(v) => edit({checkboxColor: v})} />
        ),
      },
      {
        keywords: 'dialog corner radius rounding',
        node: (
          <SliderControl
            key="dialogRadius"
            label="Dialog corner radius"
            value={draft.dialogRadius}
            min={0}
            max={20}
            display={`${draft.dialogRadius}px`}
            onChange={(v) => edit({dialogRadius: v})}
          />
        ),
      },
    ],
    signing: [
      {
        keywords: 'toolbar header background signing builder',
        node: (
          <ColorControl
            key="signHeaderBg"
            label="Toolbar background"
            value={draft.signHeaderBg}
            onChange={(v) => edit({signHeaderBg: v})}
          />
        ),
      },
      {
        keywords: 'toolbar header text color signing builder',
        node: <ColorControl key="signHeaderFg" label="Toolbar text" value={draft.signHeaderFg} onChange={(v) => edit({signHeaderFg: v})} />,
      },
      {
        keywords: 'submit button background next required',
        node: <ColorControl key="btnBg" label="Button background" value={draft.btnBg} onChange={(v) => edit({btnBg: v})} />,
      },
      {
        keywords: 'submit button label text color',
        node: <ColorControl key="btnFg" label="Button label" value={draft.btnFg} onChange={(v) => edit({btnFg: v})} />,
      },
      {
        keywords: 'button corner radius rounding pill',
        node: (
          <SliderControl
            key="btnRadius"
            label="Button corner radius"
            value={draft.btnRadius}
            min={0}
            max={25}
            display={draft.btnRadius >= 25 ? 'Pill' : `${draft.btnRadius}px`}
            onChange={(v) => edit({btnRadius: v})}
          />
        ),
      },
      {
        keywords: 'field background tint fill color',
        node: <ColorControl key="fieldTint" label="Field tint" value={draft.fieldTint} onChange={(v) => edit({fieldTint: v})} />,
      },
      {
        keywords: 'field tint opacity strength',
        node: (
          <SliderControl
            key="fieldAlpha"
            label="Field tint opacity"
            value={draft.fieldAlpha}
            min={0}
            max={100}
            display={`${draft.fieldAlpha}%`}
            onChange={(v) => edit({fieldAlpha: v})}
          />
        ),
      },
      {
        keywords: 'field corner radius rounding',
        node: (
          <SliderControl
            key="fieldRadius"
            label="Field corner radius"
            value={draft.fieldRadius}
            min={0}
            max={16}
            display={`${draft.fieldRadius}px`}
            onChange={(v) => edit({fieldRadius: v})}
          />
        ),
      },
      {
        keywords: 'required field border color',
        node: <ColorControl key="reqBorder" label="Required field border" value={draft.reqBorder} onChange={(v) => edit({reqBorder: v})} />,
      },
      {
        keywords: 'signer one color recipient palette',
        node: <ColorControl key="signer1" label="Signer 1 color" value={draft.signer1} onChange={(v) => edit({signer1: v})} />,
      },
      {
        keywords: 'signer two color recipient palette',
        node: <ColorControl key="signer2" label="Signer 2 color" value={draft.signer2} onChange={(v) => edit({signer2: v})} />,
      },
      {
        keywords: 'signer three color recipient palette',
        node: <ColorControl key="signer3" label="Signer 3 color" value={draft.signer3} onChange={(v) => edit({signer3: v})} />,
      },
    ],
    advanced: [
      {
        keywords: 'advanced custom css overrides manual raw',
        node: (
          <div key="advanced">
            <p className="mb-2 text-[11px] leading-relaxed text-gray-500">
              Appended to the generated block as-is. Anything already on this brand that the controls above do not model was preserved here.
            </p>
            <textarea
              value={draft.advanced}
              onChange={(e) => setDraft({...draft, advanced: e.target.value})}
              rows={10}
              spellCheck={false}
              className="w-full rounded-lg border border-gray-200 bg-gray-50 p-2.5 font-mono text-[11px] leading-relaxed focus:border-indigo-400 focus:outline-none"
              placeholder={'/* .verdocs-custom-theme { ... } */'}
            />
          </div>
        ),
      },
    ],
  };

  const leave = (then: () => void) => (unsaved ? setPendingLeave(() => then) : then());

  return (
    <BuilderFrame
      brand={brand}
      name={brand.name || brand.key}
      logoUrl={logoUrl}
      label="Theme Editor"
      dirty={dirty}
      working={working}
      onClose={() => leave(onBack)}
      onDiscard={handleDiscard}
      onSaveDraft={handleSaveDraft}
      onPublish={handlePublish}
      tools={
        <>
          <button
            onClick={openThemes}
            className="flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-semibold text-gray-600 hover:border-indigo-300 hover:text-indigo-600">
            <SwatchIcon className="size-4" />
            {themeName}
          </button>
        </>
      }>
      <div className="flex min-h-0 flex-1 flex-col gap-4 laptop:flex-row">
        <div className="relative flex w-full shrink-0 flex-col overflow-hidden rounded-xl border border-gray-200 laptop:w-[330px]">
          <div className="border-b border-gray-100 p-2.5">
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search controls"
              className="w-full rounded-lg border border-gray-200 px-2.5 py-1.5 text-xs focus:border-indigo-400 focus:outline-none"
            />
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto">
            {BUILDER_SECTIONS.map((section) => {
              const visible = controls[section.id].filter((c) => shows(c.keywords));
              if (query && visible.length === 0) return null;
              const isOpen = query ? true : openSection === section.id;
              const edited = sectionDirtyCount(section.keys);

              return (
                <div key={section.id} className="border-b border-gray-100 last:border-b-0">
                  <button
                    onClick={() => setOpenSection(isOpen && !query ? '' : section.id)}
                    className="flex w-full items-center gap-2 px-3.5 py-3 text-left text-[13px] font-semibold text-gray-700 hover:bg-gray-50">
                    {section.label}
                    {edited > 0 && (
                      <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-700">{edited} edited</span>
                    )}
                    <span className="flex-1" />
                    <ChevronUpIcon className={`size-4 text-gray-300 transition-transform ${isOpen ? '' : 'rotate-180'}`} />
                  </button>
                  {isOpen && <div className="px-3.5 pb-4">{visible.map((c) => c.node)}</div>}
                </div>
              );
            })}
          </div>

          {themeOpen && (
            <div className="absolute inset-0 z-20 flex flex-col bg-white">
              <div className="flex items-center gap-2 border-b border-gray-100 px-3.5 py-2.5">
                <b className="flex-1 text-[13px] text-gray-800">Themes</b>
                <button
                  onClick={revertThemes}
                  className="rounded-lg border border-gray-200 px-2.5 py-1 text-[11px] font-semibold text-gray-600">
                  Revert
                </button>
                <button
                  onClick={() => setThemeOpen(false)}
                  className="rounded-lg bg-indigo-600 px-2.5 py-1 text-[11px] font-semibold text-white">
                  Keep
                </button>
              </div>
              <p className="px-3.5 py-2 text-[11px] leading-relaxed text-gray-500">
                Click any theme to try it live. Your radii, logo, and advanced CSS are untouched. Revert restores what you had when you
                opened this panel.
              </p>
              <div className="min-h-0 flex-1 overflow-y-auto px-2.5 pb-3">
                {BRAND_THEMES.map((theme) => (
                  <button
                    key={theme.name}
                    onClick={() => {
                      setDraft(applyTheme(draft, theme));
                      setThemeName(theme.name);
                    }}
                    className={`mb-1.5 flex w-full items-center gap-2.5 rounded-lg border p-2 text-left transition-colors ${
                      themeName === theme.name ? 'border-indigo-400 bg-indigo-50/40' : 'border-transparent hover:bg-gray-50'
                    }`}>
                    <span className="w-[52px] shrink-0 overflow-hidden rounded border border-gray-200">
                      <span className="flex h-4 items-center justify-end px-1" style={{background: theme.headerBg}}>
                        <i className="h-1.5 w-4 rounded-sm" style={{background: theme.btnBg}} />
                      </span>
                      <span className="flex h-5 items-center bg-white px-1">
                        <i className="h-1.5 w-3 rounded-sm" style={{background: theme.primary}} />
                      </span>
                    </span>
                    <span className="min-w-0 flex-1">
                      <b className="block truncate text-xs font-semibold text-gray-800">{theme.name}</b>
                      <span className="text-[10.5px] capitalize text-gray-400">{theme.tag}</span>
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-gray-200">
          <div className="min-h-0 flex-1 overflow-y-auto p-3">
            <BrandPreview brand={previewBrand} />
          </div>

          <div className="border-t border-gray-100 bg-gray-50">
            <div className="flex items-center gap-2.5 px-3 py-2">
              <button onClick={() => setCssOpen(!cssOpen)} className="flex flex-1 items-center gap-2.5 text-left">
                <code className="font-mono text-[11px] text-indigo-400">&lt;/&gt;</code>
                <span className="text-xs font-semibold text-gray-700">Generated CSS overrides</span>
                <span className="font-mono text-[11px] text-gray-400">· {css.split('\n').length} lines</span>
                <span className="hidden text-[11px] text-gray-400 tablet:inline">The previews render from this exact block</span>
                <span className="flex-1" />
                <ChevronUpIcon className={`size-4 text-gray-400 transition-transform ${cssOpen ? '' : 'rotate-180'}`} />
              </button>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(css);
                  toast.success('CSS copied');
                }}
                className="flex items-center gap-1.5 rounded-md border border-gray-200 bg-white px-2 py-1 text-[11px] font-semibold text-gray-500 hover:border-indigo-300 hover:text-indigo-600">
                <ClipboardDocumentIcon className="size-3.5" />
                Copy CSS
              </button>
            </div>
            {cssOpen && (
              <pre className="max-h-[240px] overflow-auto border-t border-gray-100 bg-white px-3.5 py-3 font-mono text-[11px] leading-relaxed text-gray-600">
                {css}
              </pre>
            )}
          </div>
        </div>
      </div>

      {working && (
        <div className="absolute inset-0 flex z-40 bg-[#ffffff7f] items-center justify-center">
          <Spinner mode="dark" />
        </div>
      )}

      {pendingLeave && (
        <LeaveDialog
          onStay={() => setPendingLeave(null)}
          onLeave={pendingLeave}
          onSaveAndLeave={() => {
            handleSaveDraft();
            pendingLeave();
          }}
        />
      )}
    </BuilderFrame>
  );
};
